import type {
	ConnectionItem,
	ConnectorDefinitionItem,
	FeedItem,
} from "@/lib/api/connections";
import {
	formatStampTime,
	latestStamp,
	parseStamp,
} from "@/lib/automations/automation-status";
import { connectionHealth } from "./connections-browser";
import { feedAttentionLabel, feedAttentionTone } from "./connector-scope-lists";

/** A feed problem the user has to act on; paused and run-on-demand feeds are not. */
function feedProblem(feed: FeedItem): boolean {
	if (feed.attention === "no_trigger" || feed.attention === "paused")
		return false;
	const tone = feedAttentionTone(feed);
	return tone === "destructive" || tone === "amber";
}

type ConnectionStatus = {
	/** One plain-language line: the problem, a broken feed, or the last sync. */
	text: string;
	problem: boolean;
	/** The newest feed sync. */
	at: number | null;
};

/**
 * How a connection stands, read the same way wherever it is listed (Home,
 * Settings › Connectors): its own problem first, then a broken feed, then
 * when it last synced.
 */
export function connectionStatus(
	connection: ConnectionItem,
	feeds: FeedItem[],
	connector: ConnectorDefinitionItem | undefined,
	now: number,
): ConnectionStatus {
	const health = connectionHealth(connection, connector);
	const broken = feeds.find(feedProblem);
	const at = latestStamp(...feeds.map((feed) => parseStamp(feed.last_sync_at)));
	const feedCount = feeds.length || connection.feed_count || 0;
	let text: string;
	if (health.needsAttention && health.status) {
		text = health.status;
	} else if (broken) {
		const label = feedAttentionLabel(broken) ?? "Needs attention";
		text =
			feeds.length > 1
				? `${broken.display_name?.trim() || broken.feed_key}: ${label}`
				: label;
	} else if (at != null) {
		text = `Synced ${formatStampTime(at, now)} · ${feedCount} ${feedCount === 1 ? "feed" : "feeds"}`;
	} else if (feedCount > 0) {
		text = "Not synced yet";
	} else {
		text = connection.has_operations ? "Actions only, no feeds" : "No feeds";
	}
	return { text, problem: health.needsAttention || broken != null, at };
}
